export interface PullRequestState {
  readonly enqueueCommand: <Result>(
    environmentId: string,
    execute: () => Promise<Result>,
  ) => Promise<Result>;
  readonly read: <Result>(key: string, load: () => Promise<Result>) => Promise<Result>;
  readonly commandLaneCount: () => number;
}

/**
 * Client-side ordering and in-flight sharing for pull-request work.
 *
 * The server remains the durable cache and invalidation authority. This state is deliberately
 * bounded to promises that are currently executing: writes are ordered per environment, while
 * identical page/file reads share one request and disappear as soon as they settle.
 */
export function createPullRequestState(): PullRequestState {
  const commandLanes = new Map<string, Promise<void>>();
  const reads = new Map<string, Promise<unknown>>();

  const enqueueCommand = <Result>(
    environmentId: string,
    execute: () => Promise<Result>,
  ): Promise<Result> => {
    const preceding = commandLanes.get(environmentId) ?? Promise.resolve();
    const result = preceding.then(execute, execute);
    const settled = result.then(
      () => undefined,
      () => undefined,
    );
    commandLanes.set(environmentId, settled);
    return result.finally(() => {
      if (commandLanes.get(environmentId) === settled) commandLanes.delete(environmentId);
    });
  };

  const read = <Result>(key: string, load: () => Promise<Result>): Promise<Result> => {
    const existing = reads.get(key);
    if (existing !== undefined) return existing as Promise<Result>;

    const result = Promise.resolve().then(load);
    reads.set(key, result);
    void result.then(
      () => {
        if (reads.get(key) === result) reads.delete(key);
      },
      () => {
        if (reads.get(key) === result) reads.delete(key);
      },
    );
    return result;
  };

  return {
    enqueueCommand,
    read,
    commandLaneCount: () => commandLanes.size,
  };
}
